/**
 * About Page Content Repository
 *
 * Centralized content for the About Neuropedagogy page.
 * Contains all text, definitions, and citations.
 */

export const aboutContent = {
  // ===========================================================================
  // Hero Section
  // ===========================================================================
  hero: {
    title: "The Science Behind LRB",
    subtitle: "Understanding why neuropedagogy powers every tutoring interaction",
    tagline: "Where neuroscience meets education to transform how students learn"
  },

  // ===========================================================================
  // Section 1: What is Neuropedagogy
  // ===========================================================================
  whatIsNeuropedagogy: {
    title: "What is Neuropedagogy?",
    definition: `Neuropedagogy is the interdisciplinary field where neuroscience and education meet. Its goal is to construct the educational process by considering data on brain development, effective learning methods, and the individual peculiarities of each student.`,
    distinction: {
      title: "Evidence-Informed, Not Just Evidence-Based",
      content: `LRB moves beyond strictly following rigid scientific studies to evidence-informed teaching, where tutors and AI plan and adjust methods based on the biological factors that lead to better learning.`
    },
    keyPrinciples: [
      {
        term: "Neuroplasticity",
        explanation: "The brain is in continuous transformation; neurons develop more or fewer connections based on experience. Every tutoring session is an opportunity to facilitate this 'rewiring'."
      },
      {
        term: "Active Construction",
        explanation: "Real learning is 'learning by thinking' — an active process where students contrast new information with what they already know, literally rewiring neural connections."
      },
      {
        term: "Cognitive Load Management",
        explanation: "Working memory is a temporary buffer that can only hold 4±1 items at once. LRB ensures we never saturate this limited mental space."
      }
    ],
    railNetworkAnalogy: {
      intro: "Think of the student's brain as a high-speed rail network:",
      elements: [
        {
          id: "attention",
          label: "Attention",
          icon: "signal",
          description: "The signal operator deciding which train (information) gets onto the tracks"
        },
        {
          id: "emotions",
          label: "Emotions",
          icon: "cloud",
          description: "The weather system — a storm (stress) can shut down the whole network"
        },
        {
          id: "memory",
          label: "Memory",
          icon: "stations",
          description: "Existing stations — you can't build a new station in the middle of nowhere; it must connect to the existing line"
        },
        {
          id: "platform",
          label: "LRB Platform",
          icon: "dispatch",
          description: "The central dispatch system, ensuring tracks are clear, weather is monitored, and every new station integrates perfectly"
        }
      ]
    }
  },

  // ===========================================================================
  // Section 2: Brain Anatomy Basics
  // ===========================================================================
  brainAnatomy: {
    title: "Brain Anatomy Basics",
    intro: "To understand how LRB optimizes learning, you need to understand the key brain regions involved:",
    regions: [
      {
        id: "cerebrum",
        name: "Cerebrum",
        nickname: "The Bulk",
        color: "#1F93FE",
        function: "Higher-order thinking, consciousness, perception, and memory storage",
        lrbConnection: "Session planning targets the prefrontal cortex through structured, goal-oriented activities",
        technique: "Metacognition"
      },
      {
        id: "frontal",
        name: "Frontal Lobe",
        nickname: "The President",
        color: "#667eea",
        function: "Behavior planning, emotional control, reasoning, and decision-making",
        lrbConnection: "We scaffold difficult tasks into manageable chunks to support executive function",
        technique: "Chunking"
      },
      {
        id: "temporal",
        name: "Temporal Lobe",
        nickname: "The Archive",
        color: "#764ba2",
        function: "Houses the hippocampus (memory center) and processes auditory information",
        lrbConnection: "We build explicit bridges between new concepts and prior knowledge",
        technique: "Connection Building"
      },
      {
        id: "hippocampus",
        name: "Hippocampus",
        nickname: "Memory Central",
        color: "#10b981",
        function: "Converts short-term memories into long-term storage; essential for learning",
        lrbConnection: "Spaced retrieval strengthens hippocampal encoding for lasting retention",
        technique: "Spaced Retrieval"
      },
      {
        id: "amygdala",
        name: "Amygdala",
        nickname: "The Alarm",
        color: "#F7521B",
        function: "Processes emotions, especially fear; triggers fight-or-flight response",
        lrbConnection: "We maintain emotional safety so learning isn't blocked by anxiety",
        technique: "Safe Learning Space"
      }
    ]
  },

  // ===========================================================================
  // Section 3: Learning Theory
  // ===========================================================================
  learningTheory: {
    title: "The Science of Learning",
    intro: "These cognitive science principles guide every LRB interaction:",
    concepts: [
      {
        name: "Active Construction",
        icon: "build",
        description: "The brain builds knowledge through active engagement, not passive reception. When students actively think and contrast new information with existing knowledge, they literally rewire neural connections.",
        example: "Our Whiteboard AI encourages students to draw their thinking, making abstract concepts concrete through active participation.",
        citation: 1
      },
      {
        name: "Working Memory Limits",
        icon: "buffer",
        description: "Working memory is a temporary buffer that can only hold about 4±1 items at once. If overloaded through stress or multitasking, information is lost before it can be encoded.",
        example: "LRB keeps explanations concise and uses visual aids to offload working memory, preventing cognitive overflow.",
        citation: 2
      },
      {
        name: "Cognitive Load Theory",
        icon: "scale",
        description: "Every learning task has intrinsic load (complexity), extraneous load (poor design), and germane load (schema building). We minimize extraneous load to maximize learning.",
        example: "AI-generated summaries filter out noise, presenting only the information that matters for building understanding.",
        citation: 2
      },
      {
        name: "Spaced Retrieval",
        icon: "calendar",
        description: "Information is better retained when practice is spaced over time rather than massed together. Each retrieval strengthens the memory trace.",
        example: "The Session Planner suggests returning to topics at optimal intervals, building lasting knowledge.",
        citation: 3
      },
      {
        name: "Interleaved Practice",
        icon: "shuffle",
        description: "Mixing different topics (interleaving) produces better long-term retention than practicing one topic at a time (blocking), though it feels harder in the moment.",
        example: "Instead of 20 algebra problems then 20 geometry, we mix them — building discrimination and transfer skills.",
        citation: 3
      }
    ]
  },

  // ===========================================================================
  // Section 4: Platform Features
  // ===========================================================================
  platformFeatures: {
    title: "The LRB Platform",
    intro: "How we translate neuroscience into an integrated tutoring workflow:",
    workflow: {
      title: "The Automated Pipeline",
      steps: [
        { id: "meet", label: "Meet", icon: "video", description: "Google Meet tutoring session" },
        { id: "transcript", label: "Sync", icon: "sync", description: "One-click transcript extraction" },
        { id: "link", label: "Link", icon: "link", description: "Smart-link to student profiles" },
        { id: "summary", label: "Summarize", icon: "document", description: "AI-generated session summary" },
        { id: "plan", label: "Plan", icon: "calendar", description: "Context-aware lesson planning" },
        { id: "teach", label: "Teach", icon: "whiteboard", description: "AI-assisted tutoring" }
      ]
    },
    features: [
      {
        name: "Automated Pipeline",
        icon: "flow",
        description: "From Google Meet session to actionable insights in one click. We take the session, sync the transcript, auto-generate a summary, and feed it directly into the Session Planner.",
        technique: "Cognitive Load Management",
        benefit: "Tutors spend less time on admin and more time on what matters — helping students learn."
      },
      {
        name: "Resource Aggregation",
        icon: "aggregate",
        description: "A 'NotebookLM-like interface' where tutors can select a student and instantly see every transcript, summary, and material they've ever used. No forgotten lessons — total data continuity.",
        technique: "Connection Building",
        benefit: "The AI has the full picture to make informed, personalized suggestions based on learning history."
      },
      {
        name: "Smart Matchmaking",
        icon: "match",
        description: "We don't just throw students at tutors. Our matching system uses scores based on subjects, learning styles, and communication preferences, with Admin approval for quality assurance.",
        technique: "Social Brain",
        benefit: "Great tutoring is about connection. We ensure 'Brainiacs' and 'Team Captains' are paired for optimal learning relationships."
      }
    ]
  },

  // ===========================================================================
  // Section 5: Five Pillars
  // ===========================================================================
  fivePillars: {
    title: "The Five Pillars of LRB",
    intro: "Every AI interaction is guided by these messaging pillars:",
    pillars: [
      {
        id: "beyond",
        title: "Beyond Tutoring",
        tagline: "It's not just about grades",
        description: "Community, games, and holistic development. We include collaborative elements and gamification cues because learning should be engaging.",
        icon: "community",
        color: "#1F93FE"
      },
      {
        id: "neuro",
        title: "Neuro-Informed Advantage",
        tagline: "We explain the 'why'",
        description: "Brain-friendly strategies like chunking and spaced retrieval, with transparent explanations of the learning science behind every suggestion.",
        icon: "brain",
        color: "#667eea"
      },
      {
        id: "resilience",
        title: "Resilience & Mindset",
        tagline: "Struggle means growth",
        description: "Growth mindset principles borrowed from sports psychology. We reframe challenges positively and celebrate effort, not just results.",
        icon: "mountain",
        color: "#764ba2"
      },
      {
        id: "accessible",
        title: "Accessible Excellence",
        tagline: "Personalized for everyone",
        description: "High-quality, personalized support that adapts to individual learning styles and needs. Excellence shouldn't be exclusive.",
        icon: "key",
        color: "#10b981"
      },
      {
        id: "connect",
        title: "Connect & Grow",
        tagline: "Learning is social",
        description: "Humans learn better through interaction and cooperation. We leverage the 'Social Brain' through collaborative activities.",
        icon: "network",
        color: "#F7521B"
      }
    ]
  },

  // ===========================================================================
  // Section 6: Neuromyths
  // ===========================================================================
  neuromyths: {
    title: "Neuromyths We Avoid",
    intro: "Part of being 'Neuro-Informed' means not falling for pseudoscientific myths that lead to ineffective teaching:",
    myths: [
      {
        myth: "We only use 10% of our brain",
        reality: "We use much more than 10%; the brain functions as a coordinated, interconnected unit. Imaging studies show activity across multiple regions for even simple tasks.",
        source: "Jarrett, C. (2015). Great Myths of the Brain.",
        citation: 4
      },
      {
        myth: "Learning styles (VAK) determine how we should teach",
        reality: "While people have preferences (visual, auditory, kinesthetic), there is zero empirical evidence that teaching to a 'preferred style' improves outcomes. The brain learns best through multiple sensory modes.",
        source: "Pashler et al. (2008). Learning Styles: Concepts and Evidence.",
        citation: 5
      },
      {
        myth: "You're either 'left-brained' (logical) or 'right-brained' (creative)",
        reality: "Both hemispheres work together via the corpus callosum. Logic and creativity involve distributed networks across the whole brain.",
        source: "Nielsen et al. (2013). An Evaluation of the Left-Brain vs. Right-Brain Hypothesis.",
        citation: 6
      },
      {
        myth: "Intelligence is fixed at birth",
        reality: "The brain exhibits neuroplasticity throughout life. Intelligence can be developed through effort, strategies, and quality instruction — the foundation of growth mindset.",
        source: "Dweck, C. (2006). Mindset: The New Psychology of Success.",
        citation: 7
      },
      {
        myth: "Blocked practice (one topic at a time) is the best way to learn",
        reality: "Massed practice feels easier but produces worse retention. Interleaved practice — mixing topics — leads to better long-term learning, even though it feels harder.",
        source: "Rohrer & Taylor (2007). The shuffling of mathematics problems improves learning.",
        citation: 8
      }
    ]
  },

  // ===========================================================================
  // Sources & Citations
  // ===========================================================================
  citations: [
    {
      id: 1,
      text: "Tokuhama-Espinosa, T. (2011). Mind, Brain, and Education Science: A Comprehensive Guide to the New Brain-Based Teaching. W.W. Norton & Company.",
      url: null,
      category: "Foundation"
    },
    {
      id: 2,
      text: "Sweller, J. (1988). Cognitive Load During Problem Solving: Effects on Learning. Cognitive Science, 12(2), 257-285.",
      url: "https://doi.org/10.1207/s15516709cog1202_4",
      category: "Cognitive Load"
    },
    {
      id: 3,
      text: "Rohrer, D., & Pashler, H. (2010). Recent Research on Human Learning Challenges Conventional Instructional Strategies. Educational Researcher, 39(5), 406-412.",
      url: "https://doi.org/10.3102/0013189X10374770",
      category: "Learning Strategies"
    },
    {
      id: 4,
      text: "Jarrett, C. (2015). Great Myths of the Brain. Wiley-Blackwell.",
      url: null,
      category: "Neuromyths"
    },
    {
      id: 5,
      text: "Pashler, H., McDaniel, M., Rohrer, D., & Bjork, R. (2008). Learning Styles: Concepts and Evidence. Psychological Science in the Public Interest, 9(3), 105-119.",
      url: "https://doi.org/10.1111/j.1539-6053.2009.01038.x",
      category: "Neuromyths"
    },
    {
      id: 6,
      text: "Nielsen, J. A., et al. (2013). An Evaluation of the Left-Brain vs. Right-Brain Hypothesis with Resting State Functional Connectivity Magnetic Resonance Imaging. PLOS ONE, 8(8), e71275.",
      url: "https://doi.org/10.1371/journal.pone.0071275",
      category: "Neuromyths"
    },
    {
      id: 7,
      text: "Dweck, C. S. (2006). Mindset: The New Psychology of Success. Random House.",
      url: null,
      category: "Growth Mindset"
    },
    {
      id: 8,
      text: "Rohrer, D., & Taylor, K. (2007). The shuffling of mathematics problems improves learning. Instructional Science, 35(6), 481-498.",
      url: "https://doi.org/10.1007/s11251-007-9015-8",
      category: "Learning Strategies"
    },
    {
      id: 9,
      text: "LeDoux, J. (2000). Emotion Circuits in the Brain. Annual Review of Neuroscience, 23, 155-184.",
      url: "https://doi.org/10.1146/annurev.neuro.23.1.155",
      category: "Emotional Brain"
    },
    {
      id: 10,
      text: "Posner, M. I., & Rothbart, M. K. (2007). Research on Attention Networks as a Model for the Integration of Psychological Science. Annual Review of Psychology, 58, 1-23.",
      url: "https://doi.org/10.1146/annurev.psych.58.110405.085516",
      category: "Attention"
    }
  ],

  furtherReading: [
    {
      title: "Make It Stick: The Science of Successful Learning",
      authors: "Brown, Roediger, & McDaniel (2014)",
      description: "Practical applications of learning science research"
    },
    {
      title: "How We Learn: The Surprising Truth About When, Where, and Why It Happens",
      authors: "Carey, B. (2014)",
      description: "Accessible overview of memory and learning research"
    },
    {
      title: "Why Don't Students Like School?",
      authors: "Willingham, D. T. (2009)",
      description: "Cognitive science applied to classroom practice"
    }
  ]
};

// Navigation items for scroll-spy
export const navigationItems = [
  { id: "hero", label: "Introduction" },
  { id: "what-is", label: "What is Neuropedagogy?" },
  { id: "brain", label: "Brain Anatomy" },
  { id: "learning", label: "Learning Science" },
  { id: "platform", label: "The Platform" },
  { id: "pillars", label: "Five Pillars" },
  { id: "myths", label: "Neuromyths" },
  { id: "sources", label: "Sources" }
];

export default aboutContent;
